// src/components/gestionEvento/PruebasTable.js

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'; // Añadido useCallback 
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Toolbar,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  TablePagination, Grid, InputBase, Badge
} from '@mui/material';
import { Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
// SaveIcon ya no es necesario aquí
import PruebaRow from './PruebaRow';
import PropTypes from 'prop-types'; 
import SaveIcon from '@mui/icons-material/Save';

// predefinedPruebas ya no es necesario aquí

/**
 * Componente de tabla para mostrar y gestionar Pruebas con selección de filas y paginación.
 * Asume que la edición se maneja dentro de cada PruebaRow.
 */
const PruebasTable = ({
  pruebas,
  categorias,
  onUpdatePrueba, // Función para actualizar una prueba (se pasa a PruebaRow)
  onDeletePrueba, // Función para eliminar una prueba (se pasa a PruebaRow y se usa para eliminación masiva)
  setSnackbar,    // Función para mostrar notificaciones (se pasa a PruebaRow)
  refreshPruebas, // Función para refrescar la lista de pruebas después de una operación (actualización o eliminación)
}) => {
  // console.log('Pruebas para renderizar en PruebasTable:', pruebas); // Para depuración

  // --- Estados ---
  const [selectedPruebas, setSelectedPruebas] = useState([]); // IDs de las pruebas seleccionadas
  const [searchTerm, setSearchTerm] = useState('');           // Término de búsqueda
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false); // Control del diálogo de confirmación de borrado
  const [page, setPage] = useState(0);                       // Página actual para paginación
  const [rowsPerPage, setRowsPerPage] = useState(10);        // Filas por página
  const [isLoading, setIsLoading] = useState(false);         // Estado de carga para borrado masivo
  const [localSearch, setLocalSearch] = useState('');
  const searchRef = useRef(null);


  const [dirtyRows, setDirtyRows] = useState({}); // { [id]: { hasChanges, isValid, errors, payload } }
  const nameColWidth = { xs: 140, sm: 180, md: 240, lg: 280 };      // “Nombre de la Prueba”
  const catColMinWidth = { xs: 220, sm: 260, md: 300, lg: 320 }; 
  const NAME_COL_PX = 250;   // ancho fijo para "Nombre de la Prueba"
  const CAT_COL_PX  = 360;   // ancho sugerido para cada columna de chips
  const CB_COL_PX   = 56;    // checkbox
  const ACT_COL_PX  = 120;   // acciones
  const DESC_COL_PX = 200;   // descripción
  
  const handleRowDirtyChange = (id, info) => {
    setDirtyRows(prev => {
      const next = { ...prev };
      if (!info?.hasChanges) delete next[id];
      else next[id] = info;
      return next;
    });
  };
  const dirtyList  = Object.entries(dirtyRows);
  const dirtyValid = dirtyList.filter(([, v]) => v?.hasChanges && v?.isValid);
  const dirtyCount = dirtyList.filter(([, v]) => v?.hasChanges).length;
  const bulkMode   = dirtyCount >= 2;           // 👈 como en Categorías, el botón de guardar aparece con 2+
  const selectionLocked = dirtyCount > 0;       // 👈 bloquea selección si hay edición en curso (igual a Categorías)
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  

  const handleSaveAll = async () => {
   if (dirtyValid.length === 0) return;
   setIsBulkSaving(true);
   let ok = 0;
   const fails = [];

   // Guardado secuencial para control y feedback
   for (const [rowId, { payload }] of dirtyValid) {
    try {
      const res = await onUpdatePrueba(rowId, payload, { silent: true, skipReload: true });
      if (res?.ok) ok++; else fails.push({ rowId, reason: res?.error?.message || 'Error desconocido' });
       setDirtyRows(prev => {
         const n = { ...prev };
         delete n[rowId];
         return n;
       });
     } catch (e) {
       fails.push({ rowId, reason: e?.message || 'Error desconocido' });
     }
   }
   // 🔁 Una sola recarga al final (si pasaste refreshPruebas):
    try {
      await refreshPruebas?.();
    } catch (e) {
      console.error('Error refrescando pruebas tras bulk:', e);
    }

   if (ok > 0) {
     setSnackbar?.({ open: true, message: `Guardadas ${ok} prueba(s).`, severity: 'success' });
   }
   if (fails.length > 0) {
     setSnackbar?.({
       open: true,
       message: `Falló guardar ${fails.length}. ${fails.map(f => `#${f.rowId}`).join(', ')}`,
       severity: 'warning',
     });
   }
   setIsBulkSaving(false);
 };


  // --- Selección ---
useEffect(() => { setLocalSearch(searchTerm); }, []);
useEffect(() => {
  const t = setTimeout(() => setSearchTerm(localSearch), 250);
  return () => clearTimeout(t);
}, [localSearch]);

// atajo de teclado Ctrl/⌘ + K para enfocar
useEffect(() => {
  const onKey = (e) => {
    const isMac = navigator.platform.toUpperCase().includes('MAC');
    if ((isMac && e.metaKey && e.key.toLowerCase() === 'k') ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')) {
      e.preventDefault();
      searchRef.current?.querySelector('input')?.focus();
    }
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, []);
  /**
   * Maneja la selección/deselección de todas las filas visibles filtradas.
   * (Considera si debería seleccionar TODAS las pruebas o solo las filtradas/paginadas)
   * Esta versión selecciona todas las pruebas existentes que coinciden con el filtro.
   */
  const handleSelectAllClick = (event) => {
    if (selectionLocked) return;
    if (event.target.checked) {
      // Selecciona los IDs de todas las pruebas filtradas
      const newSelecteds = filteredPruebas.map((pru) => pru.id);
      setSelectedPruebas(newSelecteds);
      return;
    }
    setSelectedPruebas([]); // Deselecciona todo
  };

  /**
   * Maneja la selección de una fila individual.
   * Memoizado con useCallback para estabilizar la referencia pasada a PruebaRow.
   */
  const handleClick = useCallback((event, id) => {
    setSelectedPruebas((prevSelected) => {
      const selectedIndex = prevSelected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) { // No estaba seleccionado, añadir
        newSelected = newSelected.concat(prevSelected, id);
      } else if (selectedIndex === 0) { // Era el primero, quitarlo
        newSelected = newSelected.concat(prevSelected.slice(1));
      } else if (selectedIndex === prevSelected.length - 1) { // Era el último, quitarlo
        newSelected = newSelected.concat(prevSelected.slice(0, -1));
      } else if (selectedIndex > 0) { // Estaba en medio, quitarlo
        newSelected = newSelected.concat(
          prevSelected.slice(0, selectedIndex),
          prevSelected.slice(selectedIndex + 1)
        );
      }
      return newSelected; // Devuelve el nuevo array de seleccionados
    });
  }, []); // No tiene dependencias externas, solo la función setState

  /**
   * Verifica si una prueba está seleccionada.
   */
  const isSelected = (id) => selectedPruebas.indexOf(id) !== -1;

  // --- Eliminación Masiva ---

  /**
   * Inicia el proceso de eliminación de las pruebas seleccionadas.
   */
  const handleDeleteSelected = () => {
    if (selectedPruebas.length === 0) return;
    setOpenConfirmDialog(true); // Abre el diálogo de confirmación
  };

  /**
   * Confirma y ejecuta la eliminación de las pruebas seleccionadas.
   */
  const handleConfirmDelete = async () => {
    setOpenConfirmDialog(false); // Cierra el diálogo inmediatamente
    setIsLoading(true);          // Muestra el indicador de carga

    try {
      // Llama a onDeletePrueba para cada ID seleccionado
      // Promise.allSettled podría ser útil si quieres saber cuáles fallaron específicamente
      const deletePromises = selectedPruebas.map((pruebaId) => onDeletePrueba(pruebaId));
      await Promise.all(deletePromises); // Espera a que todas las promesas terminen

      setSnackbar({
        open: true,
        message: `${selectedPruebas.length} prueba${selectedPruebas.length > 1 ? 's' : ''} eliminada${selectedPruebas.length > 1 ? 's' : ''}.`,
        severity: 'success', // Usar 'success' o 'info'
      });
      setSelectedPruebas([]); // Limpia la selección después de borrar

    } catch (error) {
      console.error("Error eliminando pruebas:", error);
      setSnackbar({
        open: true,
        message: `Error al eliminar pruebas: ${error.message || 'Error desconocido'}`,
        severity: 'error',
      });
      // Considera si quieres limpiar la selección incluso si hay un error
      // setSelectedPruebas([]);
    } finally {
      setIsLoading(false); // Oculta el indicador de carga
    }
  };

  /**
   * Cancela la operación de eliminación.
   */
  const handleCancelDelete = () => {
    setOpenConfirmDialog(false);
  };

  // --- Búsqueda y Filtrado ---

  /**
   * Maneja cambios en el campo de búsqueda.
   */
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Resetea a la primera página al buscar
  };

  /**
   * Filtra las pruebas basándose en el término de búsqueda (nombre o nombre de categoría).
   * Memoizado para evitar recálculos innecesarios si pruebas, categorias o searchTerm no cambian.
   */
  const filteredPruebas = useMemo(() => {
    if (!searchTerm) {
      return pruebas; // Devuelve todas si no hay término de búsqueda
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return pruebas.filter((pru) => {
      // Comprueba el nombre de la prueba
      const nombreMatch = pru.nombre?.toLowerCase().includes(lowerCaseSearchTerm); // Añadir optional chaining

      // Comprueba los nombres de las categorías femenil asociadas
      const categoriaFemenilMatch = pru.categoriaIdsFemenil?.some((id) => {
        const categoria = categorias.find((c) => c.id === id);
        return categoria && categoria.nombre.toLowerCase().includes(lowerCaseSearchTerm);
      });

      // Comprueba los nombres de las categorías varonil asociadas
      const categoriaVaronilMatch = pru.categoriaIdsVaronil?.some((id) => {
        const categoria = categorias.find((c) => c.id === id);
        return categoria && categoria.nombre.toLowerCase().includes(lowerCaseSearchTerm);
      });

      return nombreMatch || categoriaFemenilMatch || categoriaVaronilMatch;
    });
  }, [pruebas, categorias, searchTerm]);

  // --- Paginación ---

  /**
   * Calcula las pruebas a mostrar en la página actual.
   * Memoizado para evitar recálculos si filteredPruebas, page o rowsPerPage no cambian.
   */
  const paginatedPruebas = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredPruebas.slice(start, end);
  }, [filteredPruebas, page, rowsPerPage]);

  /**
   * Maneja el cambio de página.
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Maneja el cambio en el número de filas por página.
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Resetea a la primera página al cambiar filas por página
  };

  // --- Renderizado ---

  const numSelected = selectedPruebas.length;
  const rowCount = filteredPruebas.length; // Usar longitud de pruebas filtradas para el checkbox 'select all'

  return (
    <Box sx={{ width: '100%' }}>
      {/* Barra de Búsqueda */}
<Box
  sx={{
    position: 'sticky',
    top: 0,
    zIndex: (t) => t.zIndex.appBar,
    px: 2,
    pt: 2,
    pb: 1.5,
    mb: 1.25,
    bgcolor: 'background.default',
    backdropFilter: 'blur(6px)',
    borderBottom: (t) => `1px solid ${t.palette.divider}`,
    borderRadius: 3,
  }}
>
  <Grid container spacing={1.5} alignItems="center">
    {/* Buscador */}
    <Grid item xs={12} md={7} lg={8}>
      <Box
        ref={searchRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 999,
          px: 1,
          py: 0.5,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.8),
          border: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          boxShadow: (t) => `0 2px 10px ${alpha(t.palette.common.black, 0.06)}`,
          transition: 'box-shadow .2s, transform .05s',
          '&:focus-within': {
            boxShadow: (t) => `0 6px 22px ${alpha(t.palette.primary.main, 0.18)}`,
          },
        }}
      >
        <SearchIcon sx={{ mx: 1, opacity: 0.7 }} />
        <InputBase
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          placeholder="Buscar por nombre o categorías… (Ctrl/⌘ + K)"
          inputProps={{ 'aria-label': 'buscar pruebas' }}
          sx={{
            flex: 1,
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            py: 0.5,
          }}
        />
        {!!localSearch && (
          <IconButton
            size="small"
            onClick={() => setLocalSearch('')}
            aria-label="limpiar búsqueda"
            sx={{ mr: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Grid>

    {/* Columna derecha reservada para acciones (si luego agregamos botones) */}
    {/* Acciones (lado derecho) */}
<Grid
  item
  xs={12}
  md={5}
  lg={4}
  sx={{
    display: 'flex',
    justifyContent: { xs: 'flex-start', md: 'flex-end' },
    gap: 1,
    mt: { xs: 1, md: 0 },
  }}
>
  <Badge
    color="error"
    badgeContent={numSelected || 0}
    overlap="circular"
    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
  >
    {numSelected > 0 && !selectionLocked && (
      <Button
        onClick={handleDeleteSelected}
        startIcon={<DeleteIcon />}
        sx={(t) => ({
          borderRadius: 999,
          px: { xs: 1.25, sm: 1.75 },
          py: 1,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
          bgcolor: alpha(t.palette.error.main, 0.08),
          color: t.palette.error.main,
          border: `1px solid ${alpha(t.palette.error.main, 0.24)}`,
          '&:hover': { bgcolor: alpha(t.palette.error.main, 0.12) },
        })}
      >
        Eliminar seleccionadas ({numSelected})
      </Button>
    )}
    {/* Botón Guardar en lote cuando hay 2+ filas en edición (bulkMode) */}
     {bulkMode && (
       <Button
         onClick={handleSaveAll}
         startIcon={<SaveIcon />}
         disabled={isBulkSaving || dirtyValid.length === 0}
         sx={(t) => ({
           borderRadius: 999,
           px: { xs: 1.25, sm: 1.75 },
           py: 1,
           textTransform: 'none',
           fontWeight: 600,
           fontSize: { xs: '0.8rem', sm: '0.875rem' },
           bgcolor: alpha(t.palette.success.main, 0.08),
           color: t.palette.success.main,
           border: `1px solid ${alpha(t.palette.success.main, 0.24)}`,
           '&:hover': { bgcolor: alpha(t.palette.success.main, 0.12) },
           '&.Mui-disabled': { bgcolor: alpha(t.palette.text.primary, 0.05) },
         })}
       >
         {isBulkSaving ? 'Guardando…' : `Guardar (${dirtyValid.length})`}
       </Button>
     )}
  </Badge>
</Grid>

  </Grid>
</Box>

<TableContainer
  component={Paper}
  sx={{
    borderRadius: 3,
    boxShadow: 6,
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: { xs: '150vh', md: '150vh' },
  }}
>
        {/* Tabla Principal */}
  <Table
    stickyHeader
    size="medium"
    aria-label="tabla de pruebas"
    sx={{
      minWidth: 650,
      tableLayout: 'fixed',
      // tipografías y paddings responsivos
      '& th, & td': {
        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
        py: { xs: 1, sm: 1.15, md: 1.3 },
        px: { xs: 1,   sm: 1.25, md: 1.5 },
        lineHeight: 1.3,
      },
      '& thead th': {
        bgcolor: 'background.paper',
        fontWeight: 600,
        letterSpacing: 0.2,
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        fontSize: { xs: '0.78rem', sm: '0.85rem', md: '0.95rem' },
      },
      // hover + zebra
      '& tbody tr:hover': { bgcolor: 'action.hover' },
      '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.selectedOpacity' },

      // compactar inputs internos
      '& .MuiTextField-root': { width: '100%' },
      '& .MuiInputBase-root': { height: { xs: 46, sm: 48, md: 50 } },
      '& .MuiInputBase-input': {
        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
        py: { xs: 1, sm: 1.1, md: 1.15 },
      },

      // icon buttons más compactos
      '& .MuiIconButton-root': {
        padding: { xs: 0.25, sm: 0.5, md: 0.75 },
      },

      // ancho celdas de checkbox/primera columna
      '& td.MuiTableCell-paddingCheckbox': {
        width: { xs: 40, sm: 44, md: 48 },
      },
    }}
  >
  <colgroup>
    <col style={{ width: CB_COL_PX }} />
    <col style={{ width: NAME_COL_PX }} />
    <col style={{ width: CAT_COL_PX }} />
    <col style={{ width: CAT_COL_PX }} />
    <col style={{ width: DESC_COL_PX }} />
    <col style={{ width: ACT_COL_PX }} />
  </colgroup>
          {/* Cabecera de la Tabla */}
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  // Determina si el checkbox 'select all' está marcado o indeterminado
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAllClick}
                  disabled={selectionLocked || rowCount === 0}
                  inputProps={{ 'aria-label': 'seleccionar todas las pruebas' }}
                />
              </TableCell>
              {/* Nombres de las Columnas */}
              <TableCell sx={{ width: nameColWidth, maxWidth: nameColWidth }}>Nombre de la Prueba</TableCell>
              <TableCell sx={{ minWidth: catColMinWidth }}>Categoría Femenil</TableCell>
              <TableCell sx={{ minWidth: catColMinWidth }}>Categoría Varonil</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="right">Acciones</TableCell> {/* Alinear acciones a la derecha */}
            </TableRow>
          </TableHead>

          {/* Cuerpo de la Tabla */}
          <TableBody>
            {paginatedPruebas.map((prueba) => {
              const isItemSelected = isSelected(prueba.id);
              return (
                <PruebaRow
                  key={prueba.id}
                  prueba={prueba}
                  categorias={categorias}
                  // Ya no se pasan editedData ni onEditChange
                  onUpdatePrueba={onUpdatePrueba} // Pasar la función del padre
                  onDeletePrueba={onDeletePrueba} // Pasar la función del padre
                  setSnackbar={setSnackbar}       // Pasar la función del padre
                  isSelected={isItemSelected}
                  handleClick={handleClick}       // Pasar la función memoizada
                  onRowDirtyChange={handleRowDirtyChange}
                  bulkMode={bulkMode}
                  selectionLocked={selectionLocked}
                  nameColPx={NAME_COL_PX}
                  descColPx={DESC_COL_PX}
                  catColPx={CAT_COL_PX}
                />
              );
            })}
            {/* Mensaje si no hay pruebas después de filtrar */}
            {paginatedPruebas.length === 0 && searchTerm && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No se encontraron pruebas con "{searchTerm}".
                </TableCell>
              </TableRow>
            )}
            {/* Mensaje si no hay pruebas en absoluto */}
            {pruebas.length === 0 && !searchTerm && (
               <TableRow>
                 <TableCell colSpan={5} align="center">
                   Aún no hay pruebas creadas para esta competencia.
                 </TableCell>
               </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Componente de Paginación */}
        <TablePagination
          component="div"
          count={filteredPruebas.length} // Contar sobre las pruebas filtradas
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]} // Opciones de filas por página
          labelRowsPerPage="Filas por página:"
          // Etiqueta que muestra el rango actual
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
        />
      </TableContainer>

      {/* Diálogo de Confirmación de Eliminación Masiva */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelDelete} // Permite cerrar haciendo click fuera
        aria-labelledby="confirm-delete-dialog-title"
        aria-describedby="confirm-delete-dialog-description"
      >
        <DialogTitle id="confirm-delete-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-dialog-description">
            ¿Estás seguro de eliminar {selectedPruebas.length} prueba{selectedPruebas.length > 1 ? 's' : ''} seleccionada{selectedPruebas.length > 1 ? 's' : ''}? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Indicador de Carga para Eliminación Masiva (Overlay) */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute', // Posición relativa al contenedor (TableContainer o Box padre)
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)', // Fondo semitransparente
            zIndex: 2, // Asegura que esté sobre la tabla
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Eliminando...</Typography>
        </Box>
      )}
    </Box>
  );
};

// --- PropTypes --- (Sin cambios necesarios aquí)
PruebasTable.propTypes = {
  pruebas: PropTypes.arrayOf(PropTypes.object).isRequired,
  categorias: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  onUpdatePrueba: PropTypes.func.isRequired,
  onDeletePrueba: PropTypes.func.isRequired,
  setSnackbar: PropTypes.func.isRequired,
};

export default PruebasTable;